import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  MdFactory,
  MdEmail,
  MdLock,
  MdVisibility,
  MdVisibilityOff,
  MdShield,
} from "react-icons/md";
import toast, { Toaster } from "react-hot-toast";
import ClipLoader from "react-spinners/ClipLoader";
import jwt_encode from "jwt-encode";
import { sessionManager } from "utils/sessionManager";
import { tokenManager } from "utils/tokenManager";
import { accessControl } from "utils/accessControl";
import { loginKpiUser } from "api/auth";

const INDUSTRIAL_BG =
  "https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80";

export default function SignInPerformanceKPI() {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [rememberWorkstation, setRememberWorkstation] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
    if (errors[id]) setErrors((prev) => ({ ...prev, [id]: "" }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.email.trim()) {
      newErrors.email = "Work email address is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }
    if (!formData.password.trim()) {
      newErrors.password = "Password is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      const data = await loginKpiUser({
        email: formData.email,
        password: formData.password,
      });

      if (data.message === "Login successful") {
        const secret = "your-secret-key";
        const userWithUnit = {
          ...data.user,
          unit: "Performance KPI",
          username: data.user.email || data.user.username,
        };
        const encodedUserData = jwt_encode(userWithUnit, secret);

        sessionManager.storeLoginData(encodedUserData);
        tokenManager.storeToken(
          data.token,
          data.token_type || "Bearer",
          data.expires_in ?? 86400
        );

        accessControl.refreshPermissions();

        if (rememberWorkstation) {
          localStorage.setItem("performanceKpi_rememberWorkstation", "true");
        } else {
          localStorage.removeItem("performanceKpi_rememberWorkstation");
        }

        toast.success(data.message || "Login successful!");
        setTimeout(() => navigate("/performance-kpi/dashboard"), 250);
      }
    } catch (error) {
      console.error("Login error:", error);
      toast.error(error.response?.data?.message || "Login failed");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (sessionManager.isSessionValid() && accessControl.canAccessPerformanceKPI()) {
      navigate("/performance-kpi/dashboard");
    }
  }, [navigate]);

  const industrialOverlayStyle = {
    background: `linear-gradient(rgba(26, 22, 20, 0.85), rgba(22, 101, 52, 0.4)), url('${INDUSTRIAL_BG}')`,
    backgroundSize: "cover",
    backgroundPosition: "center",
  };

  return (
    <div className="flex min-h-screen w-full flex-col bg-[#fdfcfb] font-sans dark:bg-[#1a1614] lg:flex-row">
      <Toaster position="top-right" />
      {/* Left column - promotional section (hidden on mobile) */}
      <div
        className="hidden lg:flex lg:w-1/2 relative p-16 flex-col justify-between"
        style={industrialOverlayStyle}
        data-alt="Food processing facility machinery"
      >
        <div>
          <div className="flex items-center gap-4 mb-2">
            <div className="bg-white p-3 rounded-xl shadow-xl">
              <MdFactory className="text-[#166534] text-4xl font-bold" />
            </div>
            <div>
              <span className="text-white text-3xl font-black tracking-tight block leading-none">
                USIBRAS
              </span>
              <span className="text-white/80 text-lg font-medium tracking-[0.2em] uppercase">
                Ghana Limited
              </span>
            </div>
          </div>
          <div className="h-1 w-24 bg-[#d97706] mt-4 rounded" />
        </div>
        <div className="max-w-xl">
          <h1 className="text-white text-5xl font-black leading-tight tracking-tight mb-6">
            Processing Excellence, <br />
            <span className="text-[#d97706]">Performance Driven.</span>
          </h1>
          <p className="text-white/80 text-xl font-normal leading-relaxed">
            Welcome to the performance management portal. Monitor cashew
            processing efficiency, quality standards, and operational KPIs in
            real-time.
          </p>
        </div>
        <div className="flex flex-col gap-4">
          <div className="text-white/40 text-sm">
            © 2024 Usibras Ghana Limited. All rights reserved.
          </div>
        </div>
      </div>

      {/* Right column - login form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12 bg-white dark:bg-[#1a1614]">
        <div className="w-full max-w-[480px] flex flex-col">
          <div className="flex flex-col items-center lg:items-start gap-4 mb-10">
            {/* Mobile brand - visible only on small screens */}
            <div className="flex lg:hidden items-center gap-3 mb-4">
              <MdFactory className="text-[#166534] text-4xl" />
              <div className="flex flex-col">
                <span className="text-[#1a1614] dark:text-white text-xl font-black">
                  USIBRAS
                </span>
                <span className="text-[#1a1614]/60 dark:text-gray-400 text-xs font-bold uppercase tracking-[0.2em]">
                  Ghana Limited
                </span>
              </div>
            </div>
            <h2 className="text-[#1a1614] dark:text-white text-3xl font-black leading-tight tracking-tight">
              Login Portal
            </h2>
            <p className="text-[#64748b] dark:text-gray-400 mt-2 text-center lg:text-left">
              Performance Management System Secure Access
            </p>
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col gap-5">
            <div className="flex flex-col gap-2">
              <label className="text-[#1a1614] dark:text-gray-200 text-sm font-bold uppercase tracking-wider">
                Work Email Address
              </label>
              <div className="relative">
                <MdEmail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  id="email"
                  type="email"
                  placeholder="name@usibras.com.gh"
                  value={formData.email}
                  onChange={handleChange}
                  className="flex w-full rounded-lg text-[#1a1614] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#d97706] border border-[#e2e8f0] dark:border-gray-700 bg-white dark:bg-gray-800 h-14 placeholder:text-gray-400 pl-12 pr-4 text-base font-normal transition-all"
                  required
                />
              </div>
              {errors.email && (
                <p className="text-red-500 text-xs mt-1">{errors.email}</p>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <div className="flex justify-between items-center">
                <label className="text-[#1a1614] dark:text-gray-200 text-sm font-bold uppercase tracking-wider">
                  Password
                </label>
                <a
                  className="text-[#166534] dark:text-[#d97706] text-sm font-bold hover:underline cursor-pointer"
                  onClick={() => toast("Contact IT Support to reset password")}
                  href="#"
                >
                  Forgot password?
                </a>
              </div>
              <div className="flex w-full items-stretch relative">
                <MdLock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleChange}
                  className="flex w-full rounded-lg text-[#1a1614] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#d97706] border border-[#e2e8f0] dark:border-gray-700 bg-white dark:bg-gray-800 h-14 placeholder:text-gray-400 pl-12 pr-12 text-base font-normal transition-all"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#166534] cursor-pointer"
                >
                  {showPassword ? (
                    <MdVisibilityOff className="w-5 h-5" />
                  ) : (
                    <MdVisibility className="w-5 h-5" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="text-red-500 text-xs mt-1">{errors.password}</p>
              )}
            </div>

            <div className="flex items-center py-2">
              <label className="flex items-center gap-x-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={rememberWorkstation}
                  onChange={(e) => setRememberWorkstation(e.target.checked)}
                  className="h-5 w-5 rounded border-[#e2e8f0] dark:border-gray-700 border-2 bg-transparent text-[#166534] focus:ring-0 focus:ring-offset-0 focus:outline-none"
                />
                <span className="text-[#1a1614] dark:text-gray-200 text-base font-normal">
                  Remember this workstation
                </span>
              </label>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="flex w-full cursor-pointer items-center justify-center rounded-lg h-14 px-5 bg-[#166534] text-white text-base font-bold leading-normal tracking-[0.015em] hover:bg-[#0f4625] transition-all mt-4 shadow-lg shadow-[#166534]/20 disabled:opacity-70"
            >
              {isLoading ? (
                <ClipLoader size={20} color="#ffffff" />
              ) : (
                "Sign In to Usibras CMS"
              )}
            </button>
          </form>

          <div className="mt-12 p-5 rounded-xl bg-[#f8fafc] dark:bg-gray-800/50 border border-[#e2e8f0] dark:border-gray-700">
            <div className="flex gap-4">
              <div className="bg-[#166534]/10 dark:bg-[#166534]/20 p-2 rounded-lg self-start">
                <MdShield className="text-[#166534] dark:text-[#d97706] w-6 h-6" />
              </div>
              <div className="flex flex-col gap-1">
                <p className="text-[#1a1614] dark:text-white text-sm font-bold uppercase tracking-tight">
                  Authorized Personnel Only
                </p>
                <p className="text-[#64748b] dark:text-gray-400 text-xs leading-relaxed">
                  Access to the Usibras Ghana Limited performance system is
                  restricted to Admin and Supervisor roles. All login attempts and
                  data modifications are logged for audit purposes.
                </p>
              </div>
            </div>
          </div>

          <div className="text-center mt-10">
            <p className="text-[#64748b] dark:text-gray-500 text-sm">
              Having trouble logging in? <br />
              <a
                className="text-[#166534] dark:text-[#d97706] font-bold hover:underline cursor-pointer"
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  toast("Contact Usibras IT Support Department");
                }}
              >
                Contact Usibras IT Support Department
              </a>
            </p>
          </div>

          <div className="text-center mt-6">
            <p className="text-[#94a3b8] dark:text-gray-600 text-xs">
              Powered by{" "}
              <a
                href="https://sangeotechnologies.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#166534] dark:text-[#d97706] font-bold hover:underline"
              >
                sangeotechnologies.com
              </a>
              {" "}&middot;{" "}
              <a
                href="tel:0543370183"
                className="text-[#166534] dark:text-[#d97706] font-bold hover:underline"
              >
                0543370183
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
